import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import api from '../../services/api';
import { Button, Card, Input } from '../../components/ui';
import { usePlan } from '../../hooks/usePlan';
import toast from 'react-hot-toast';

const QUESTION_TYPES = [
  { value: 'score_emotion', label: 'Score émotionnel (1-10 / emojis)' },
  { value: 'ternary', label: 'Oui / Non / Mitigé' },
  { value: 'binary', label: 'Oui / Non' },
  { value: 'text_short', label: 'Texte court' },
];

const FREQUENCIES = [
  { value: 'one_shot', label: 'Ponctuel' },
  { value: 'weekly', label: 'Hebdomadaire' },
  { value: 'monthly', label: 'Mensuel' },
];

const DEFAULT_QUESTION = { order: 0, label: '', type: 'score_emotion', is_main: true };

export default function AdminSurveyForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const { canCreateSurvey } = usePlan();

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [services, setServices] = useState([]);
  const [sites, setSites] = useState([]);

  const [form, setForm] = useState({
    title: '',
    description: '',
    date_start: '',
    date_end: '',
    frequency: 'one_shot',
    target_type: 'all',
    target_service_id: '',
    target_site_id: '',
    status: 'draft',
    min_responses_to_display: 5,
    token_count: 10,
    emails: '',
    questions: [{ ...DEFAULT_QUESTION }],
  });

  useEffect(() => {
    api.get('/admin/services').then((r) => setServices(r.data?.data || [])).catch(() => {});
    api.get('/admin/sites').then((r) => setSites(r.data?.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (isEdit) {
      api.get(`/admin/surveys/${id}`)
        .then((r) => {
          const d = r.data?.data;
          if (d) {
            setForm({
              title: d.title || '',
              description: d.description || '',
              date_start: d.date_start || '',
              date_end: d.date_end || '',
              frequency: d.frequency || 'one_shot',
              target_type: d.target_type || 'all',
              target_service_id: d.target_service_id || '',
              target_site_id: d.target_site_id || '',
              status: d.status || 'draft',
              min_responses_to_display: d.min_responses_to_display ?? 5,
              token_count: d.response_tokens_count ?? d.tokens?.length ?? 10,
              response_tokens_count: d.response_tokens_count ?? d.tokens?.length ?? 10,
              emails: '',
              questions: (d.questions || []).length > 0
                ? d.questions.map((q, i) => ({
                    id: q.id,
                    order: i,
                    label: q.label,
                    type: q.type,
                    is_main: q.is_main ?? i === 0,
                  }))
                : [{ ...DEFAULT_QUESTION }],
            });
          }
        })
        .catch(() => toast.error('Sondage non trouvé'))
        .finally(() => setLoading(false));
    }
  }, [id, isEdit]);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const addQuestion = () => {
    if (form.questions.length >= 4) return;
    setForm((f) => ({
      ...f,
      questions: [...f.questions, { order: f.questions.length, label: '', type: 'ternary', is_main: false }],
    }));
  };

  const removeQuestion = (idx) => {
    if (form.questions.length <= 1) return;
    setForm((f) => ({
      ...f,
      questions: f.questions.filter((_, i) => i !== idx).map((q, i) => ({ ...q, order: i, is_main: i === 0 })),
    }));
  };

  const updateQuestion = (idx, key, value) => {
    setForm((f) => ({
      ...f,
      questions: f.questions.map((q, i) =>
        i === idx ? { ...q, [key]: value } : (key === 'is_main' && value ? { ...q, is_main: false } : q)
      ),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const emailsRaw = (form.emails || '').trim().split(/[\n,;]+/).map((e) => e.trim()).filter(Boolean);
    const payload = {
      ...form,
      target_service_id: form.target_type === 'service' ? form.target_service_id : null,
      target_site_id: form.target_type === 'site' ? form.target_site_id : null,
      emails: emailsRaw.length > 0 ? emailsRaw : [],
      questions: form.questions.map((q, i) => ({ ...q, order: i })).filter((q) => q.label.trim()),
    };
    if (payload.questions.length === 0) {
      toast.error('Ajoutez au moins une question');
      setSaving(false);
      return;
    }
    try {
      if (isEdit) {
        await api.put(`/admin/surveys/${id}`, payload);
        toast.success('Sondage mis à jour');
      } else {
        await api.post('/admin/surveys', payload);
        toast.success('Sondage créé');
      }
      navigate('/admin/surveys');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-gray-500">Chargement...</p>;

  if (!isEdit && !canCreateSurvey) {
    return (
      <div className="max-w-2xl">
        <Link to="/admin/surveys" className="text-primary-600 hover:underline block mb-4">← Sondages</Link>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-8 text-center">
          <p className="text-amber-800 font-medium mb-2">Vous avez atteint la limite de votre plan.</p>
          <p className="text-amber-700 text-sm mb-6">Passez à l'abonnement pour créer plus de sondages.</p>
          <Link to="/pricing"><Button>Voir les tarifs</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl">
      <Link to="/admin/surveys" className="text-primary-600 hover:underline block mb-4">← Sondages</Link>
      <h1 className="text-2xl font-bold text-gray-800 mb-6">{isEdit ? 'Modifier le sondage' : 'Nouveau sondage'}</h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card title="Informations générales">
          <Input
            label="Titre"
            value={form.title}
            onChange={(e) => update('title', e.target.value)}
            required
            placeholder="Comment vous sentez-vous cette semaine ?"
          />
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => update('description', e.target.value)}
              rows={2}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              placeholder="Optionnel"
            />
          </div>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <Input
              label="Date début"
              type="date"
              value={form.date_start}
              onChange={(e) => update('date_start', e.target.value)}
              required
            />
            <Input
              label="Date fin"
              type="date"
              value={form.date_end}
              onChange={(e) => update('date_end', e.target.value)}
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fréquence</label>
              <select
                value={form.frequency}
                onChange={(e) => update('frequency', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              >
                {FREQUENCIES.map((f) => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
              <select
                value={form.status}
                onChange={(e) => update('status', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              >
                <option value="draft">Brouillon</option>
                <option value="active">Actif</option>
                <option value="closed">Clôturé</option>
              </select>
            </div>
          </div>
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Population ciblée</label>
            <select
              value={form.target_type}
              onChange={(e) => update('target_type', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            >
              <option value="all">Tous</option>
              <option value="service">Par service</option>
              <option value="site">Par site</option>
            </select>
            {form.target_type === 'service' && (
              <select
                value={form.target_service_id}
                onChange={(e) => update('target_service_id', e.target.value)}
                className="w-full mt-2 px-4 py-2 border border-gray-300 rounded-lg"
              >
                <option value="">Sélectionner un service</option>
                {services.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            )}
            {form.target_type === 'site' && (
              <select
                value={form.target_site_id}
                onChange={(e) => update('target_site_id', e.target.value)}
                className="w-full mt-2 px-4 py-2 border border-gray-300 rounded-lg"
              >
                <option value="">Sélectionner un site</option>
                {sites.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            )}
          </div>
          {!isEdit && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Emails des votants (optionnel)</label>
              <textarea
                value={form.emails}
                onChange={(e) => update('emails', e.target.value)}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                placeholder="un@exemple.fr, autre@exemple.fr (un par ligne ou séparés par virgule)"
              />
              <p className="text-xs text-gray-500 mt-1">
                Si fournis, un token sera rattaché à chaque email. Vous pourrez ensuite envoyer les liens aux votants.
              </p>
            </div>
          )}
          <div className="mt-4">
            <Input
              label={isEdit ? 'Nombre de liens (augmenter pour en ajouter)' : 'Nombre de liens à générer'}
              type="number"
              min={isEdit ? (form.response_tokens_count ?? form.token_count) : 1}
              max={10000}
              value={form.token_count}
              onChange={(e) => update('token_count', parseInt(e.target.value, 10) || (isEdit ? (form.response_tokens_count ?? 5) : 10))}
            />
            {isEdit && (
              <p className="text-xs text-gray-500 mt-1">
                Actuellement {form.response_tokens_count ?? form.token_count} lien(s). Mettez un nombre supérieur pour en ajouter (pas de suppression).
              </p>
            )}
          </div>
          <div className="mt-4">
            <Input
              label="Nombre min. de réponses pour afficher les résultats"
              type="number"
              min={1}
              max={100}
              value={form.min_responses_to_display}
              onChange={(e) => update('min_responses_to_display', parseInt(e.target.value, 10) || 5)}
            />
            <p className="text-xs text-gray-500 mt-1">Tokens uniques, base séparée — anonymat garanti</p>
          </div>
        </Card>

        <Card title="Questions (1 à 4)">
          {form.questions.map((q, idx) => (
            <div key={idx} className="mb-4 p-4 bg-gray-50 rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium">Question {idx + 1}</span>
                {form.questions.length > 1 && (
                  <button type="button" onClick={() => removeQuestion(idx)} className="text-danger-500 text-sm hover:underline">
                    Supprimer
                  </button>
                )}
              </div>
              <Input
                label="Libellé"
                value={q.label}
                onChange={(e) => updateQuestion(idx, 'label', e.target.value)}
                placeholder="Comment vous sentez-vous ?"
                required
              />
              <div className="mt-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={q.type}
                  onChange={(e) => updateQuestion(idx, 'type', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                >
                  {QUESTION_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <label className="flex items-center gap-2 mt-2">
                <input
                  type="checkbox"
                  checked={q.is_main}
                  onChange={(e) => updateQuestion(idx, 'is_main', e.target.checked)}
                />
                <span className="text-sm">Question principale (score global)</span>
              </label>
            </div>
          ))}
          {form.questions.length < 4 && (
            <button type="button" onClick={addQuestion} className="text-primary-600 hover:underline text-sm">
              + Ajouter une question
            </button>
          )}
        </Card>

        <div className="flex gap-2">
          <Button type="submit" disabled={saving}>{saving ? 'Enregistrement...' : 'Enregistrer'}</Button>
          <Link to="/admin/surveys"><Button type="button" variant="outline">Annuler</Button></Link>
        </div>
      </form>
    </div>
  );
}
